import { Box, Heading, Stack } from '@chakra-ui/react';
import type React from 'react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import Show from '@/components/Molecules/Show';
import DetailsView from '@/components/Templates/DetailsView';
import { useAuthStore } from '@/store';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftRadioGroup from '@/components/Atoms/FaceliftRadioGroup';

export interface CheckoutInvoiceSummaryProps {
	subtotal: number;
	tax: number;
	shipping: number;
	total: number;
	surcharge?: number;
	onCheckout: (type: 'session' | 'link') => void;
	isStripeSessionLoading?: boolean;
	checkoutButtonRef?: React.RefObject<HTMLButtonElement | null>;
	paymentMethod?: string;
	onPaymentMethodChange?: (method: string) => void;
}

const CheckoutInvoiceSummary: React.FC<CheckoutInvoiceSummaryProps> = ({
	subtotal,
	tax,
	shipping,
	total,
	surcharge = 0,
	onCheckout,
	isStripeSessionLoading = false,
	checkoutButtonRef,
	paymentMethod = 'card',
	onPaymentMethodChange,
}) => {
	// Get authenticated user info
	const user = useAuthStore((state) => state.user?.userInformation);

	return (
		<Box
			p={4}
			boxShadow="sm"
			border="1px solid"
			borderColor={{ base: 'gray.300', _dark: 'gray.600' }}
			borderRadius="md"
		>
			<Heading size="lg" mb={4}>
				Summary
			</Heading>
			<Stack mt={4}>
				<DetailsView
					gridConfig={{ base: '1fr', md: '1fr' }}
					data={[
						{
							data: [
								{ label: 'Subtotal', value: `$${subtotal.toFixed(2)}` },
								{ label: 'Tax', value: `$${tax.toFixed(2)}` },
								{ label: 'Shipping Cost', value: `$${shipping.toFixed(2)}` },
								{ label: 'Surcharge', value: `$${surcharge?.toFixed(2) || 0}` },
								{ label: 'Order Total', value: `$${total.toFixed(2)}` },
							],
						},
					]}
				/>
			</Stack>
			<Stack mt={4}>
				<FaceliftText fontWeight="bold">Payment Method</FaceliftText>
				<FaceliftText fontWeight="semibold" color="gray.500">Cards</FaceliftText>
				<FaceliftRadioGroup
					value={paymentMethod}
					onValueChange={onPaymentMethodChange || (() => {})}
					items={[
						{ label: 'Card', value: 'card' },
					]}
					direction="column"
					gap={3}
				/>
				<FaceliftText fontWeight="semibold" color="gray.500">Bank Transfer</FaceliftText>
				<FaceliftRadioGroup
					value={paymentMethod}
					onValueChange={onPaymentMethodChange || (() => {})}
					items={[
						{ label: 'ACH', value: 'ach' },
					]}
					direction="column"
					gap={3}
				/>
			</Stack>
			<Stack mt={6} gap={3}>
				<FaceliftButton
					ref={checkoutButtonRef}
					onClick={() => onCheckout('session')}
					colorScheme="blue"
					fontWeight={'bold'}
					disabled={isStripeSessionLoading || total === 0}
				>
					Proceed to Checkout
				</FaceliftButton>
				<Show when={user?.role === 'Admin'}>
					<FaceliftButton
						onClick={() => onCheckout('link')}
						colorScheme="blue"
						width="100%"
						fontWeight={'bold'}
						disabled={isStripeSessionLoading || total === 0}
					>
						Create Payment Link
					</FaceliftButton>
				</Show>
			</Stack>
		</Box>
	);
};
export default CheckoutInvoiceSummary;
